import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowTrendUp,
  faAward,
  faBookOpen,
  faChartLine,
  faChartColumn,
  faCircleCheck,
  faClock,
  faCreditCard,
  faEnvelope,
  faMagnifyingGlass,
  faMoneyBillTrendUp,
  faRotateRight,
  faTriangleExclamation,
  faUserGraduate,
  faUsers,
} from '@fortawesome/free-solid-svg-icons';
import { apiService } from '../../services/api';
import './AdminDashboard.css';

const currency = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
});

const number = new Intl.NumberFormat('pt-BR');

const percent = (value) => `${Number(value || 0).toLocaleString('pt-BR', {
  maximumFractionDigits: 1,
})}%`;

const date = (value) => {
  if (!value) return '-';
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(new Date(value));
};

const shortDateTime = (value) => {
  if (!value) return '-';
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
};

const safeList = (value) => Array.isArray(value) ? value : [];

const metricValue = (item) => {
  if (item.type === 'currency') return currency.format(item.value || 0);
  if (item.type === 'percent') return percent(item.value);
  return number.format(item.value || 0);
};

const StatusPill = ({ status }) => {
  const label = {
    completed: 'Aprovado',
    pending: 'Pendente',
    failed: 'Recusado',
    cancelled: 'Cancelado',
    refunded: 'Estornado',
    in_progress: 'Em andamento',
    not_passed: 'Reprovado',
  }[status] || status || '-';

  return <span className={`admin-status admin-status-${status || 'empty'}`}>{label}</span>;
};

const MetricCard = ({ item }) => (
  <article className="admin-metric-card">
    <div className="admin-metric-icon">
      <FontAwesomeIcon icon={item.icon} />
    </div>
    <div>
      <span className="admin-metric-label">{item.label}</span>
      <strong>{metricValue(item)}</strong>
    </div>
  </article>
);

const ProgressRow = ({ label, value, meta }) => (
  <div className="admin-progress-row">
    <div className="admin-progress-meta">
      <span>{label}</span>
      <strong>{percent(value)}</strong>
    </div>
    <div className="admin-progress-track" aria-hidden="true">
      <span style={{ width: `${Math.min(Number(value || 0), 100)}%` }} />
    </div>
    {meta && <small>{meta}</small>}
  </div>
);

const RankingList = ({ title, items, valueKey, valueLabel }) => (
  <section className="admin-section">
    <div className="admin-section-title">
      <h2>{title}</h2>
    </div>
    <div className="admin-list">
      {safeList(items).map((item, index) => (
        <div className="admin-list-row" key={`${title}-${item.id || index}`}>
          <span className="admin-rank">{index + 1}</span>
          <span className="admin-list-title">{item.title || item.course_title || '-'}</span>
          <strong>{number.format(Number(item[valueKey] || 0))} {valueLabel}</strong>
        </div>
      ))}
      {safeList(items).length === 0 && <div className="admin-empty">Sem dados para exibir.</div>}
    </div>
  </section>
);

const AdminDashboard = () => {
  const [dashboard, setDashboard] = useState(null);
  const [users, setUsers] = useState([]);
  const [usersPage, setUsersPage] = useState(1);
  const [usersMeta, setUsersMeta] = useState({
    current_page: 1,
    last_page: 1,
    total: 0,
    from: 0,
    to: 0,
  });
  const [selectedUser, setSelectedUser] = useState(null);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [usersLoading, setUsersLoading] = useState(false);
  const [error, setError] = useState('');
  const [reminderSendingId, setReminderSendingId] = useState(null);
  const [reminderMessage, setReminderMessage] = useState('');
  const [courseReminderSendingId, setCourseReminderSendingId] = useState(null);
  const [courseReminderMessage, setCourseReminderMessage] = useState('');

  const loadDashboard = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await apiService.admin.dashboard();
      setDashboard(response.data);
    } catch (err) {
      setError('Não foi possível carregar o painel administrativo.');
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async (term = '', page = 1) => {
    try {
      setUsersLoading(true);
      const response = await apiService.admin.users(term, page, 20);
      setUsers(response.data?.data || []);
      setUsersMeta(response.data?.meta || {
        current_page: page,
        last_page: 1,
        total: response.data?.data?.length || 0,
        from: response.data?.data?.length ? 1 : 0,
        to: response.data?.data?.length || 0,
      });
    } catch (err) {
      setUsers([]);
      setUsersMeta({
        current_page: page,
        last_page: 1,
        total: 0,
        from: 0,
        to: 0,
      });
    } finally {
      setUsersLoading(false);
    }
  };

  const loadUserDetail = async (id) => {
    try {
      const response = await apiService.admin.user(id);
      setSelectedUser(response.data);
      setReminderMessage('');
      setCourseReminderMessage('');
    } catch (err) {
      setSelectedUser(null);
    }
  };

  const canSendCertificateReminder = (payment) => (
    payment?.status === 'pending' && payment?.certificate_reminder_available
  );

  const reminderBlockedLabel = (payment) => {
    if (payment?.status !== 'pending') return '';
    if (!payment?.certificate_reminder_sent_at) return '';
    return `Disponível em ${date(payment.certificate_reminder_next_available_at)}`;
  };

  const handleCertificateReminder = async (payment) => {
    if (!payment?.id || !selectedUser) return;

    try {
      setReminderSendingId(payment.id);
      setReminderMessage('');
      const response = await apiService.admin.sendCertificateReminder(payment.id);
      const sentAt = response.data?.certificate_reminder_sent_at;
      const nextAvailableAt = response.data?.next_available_at;

      setSelectedUser((current) => {
        if (!current) return current;
        return {
          ...current,
          payments: safeList(current.payments).map((item) => (
            item.id === payment.id
              ? {
                ...item,
                certificate_reminder_sent_at: sentAt,
                certificate_reminder_next_available_at: nextAvailableAt,
                certificate_reminder_available: false,
              }
              : item
          )),
        };
      });

      setReminderMessage('E-mail de lembrete enviado com sucesso.');
    } catch (err) {
      setReminderMessage(err.response?.data?.message || 'Não foi possível enviar o lembrete.');
    } finally {
      setReminderSendingId(null);
    }
  };

  const canSendCourseReminder = (course) => (
    course?.course_exam_reminder_eligible && course?.course_reminder_available
  );

  const courseReminderBlockedLabel = (course) => {
    if (!course?.course_exam_reminder_eligible) return '';
    if (!course?.exam_reminder_sent_at) return '';
    return `Disponível em ${date(course.exam_reminder_next_available_at)}`;
  };

  const handleCourseContinuationReminder = async (course) => {
    if (!course?.enrollment_id || !selectedUser) return;

    try {
      setCourseReminderSendingId(course.enrollment_id);
      setCourseReminderMessage('');
      const response = await apiService.admin.sendCourseContinuationReminder(course.enrollment_id);
      const sentAt = response.data?.exam_reminder_sent_at;
      const nextAvailableAt = response.data?.next_available_at;

      setSelectedUser((current) => {
        if (!current) return current;
        return {
          ...current,
          courses: safeList(current.courses).map((item) => (
            item.enrollment_id === course.enrollment_id
              ? {
                ...item,
                exam_reminder_sent_at: sentAt,
                exam_reminder_next_available_at: nextAvailableAt,
                course_reminder_available: false,
              }
              : item
          )),
        };
      });

      setCourseReminderMessage(`Lembrete da prova de "${course.title}" enviado com sucesso.`);
    } catch (err) {
      setCourseReminderMessage(err.response?.data?.message || 'Não foi possível enviar o lembrete.');
    } finally {
      setCourseReminderSendingId(null);
    }
  };

  useEffect(() => {
    loadDashboard();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => loadUsers(search, usersPage), 350);
    return () => clearTimeout(timer);
  }, [search, usersPage]);

  const overviewMetrics = useMemo(() => {
    const overview = dashboard?.overview || {};
    return [
      { label: 'Usuários cadastrados', value: overview.registered_users, icon: faUsers },
      { label: 'Usuários ativos', value: overview.active_users, icon: faChartLine },
      { label: 'Novos alunos', value: overview.new_registrations, icon: faUserGraduate },
      { label: 'Cursos concluídos', value: overview.course_completions, icon: faCircleCheck },
      { label: 'Taxa de conclusão', value: overview.completion_rate, type: 'percent', icon: faArrowTrendUp },
      { label: 'Certificados vendidos', value: overview.certificates_sold, icon: faAward },
      { label: 'Receita', value: overview.certificate_revenue, type: 'currency', icon: faMoneyBillTrendUp },
      { label: 'Conversão em certificado', value: overview.certificate_conversion_rate, type: 'percent', icon: faCreditCard },
    ];
  }, [dashboard]);

  const certificate = dashboard?.certificates || {};
  const coursePerformance = dashboard?.course_performance || {};
  const maxRevenue = Math.max(...safeList(certificate.revenue_by_period).map((item) => Number(item.revenue || 0)), 1);

  if (loading) {
    return (
      <main className="admin-dashboard-page">
        <div className="admin-loading">
          <FontAwesomeIcon icon={faRotateRight} spin />
          <span>Carregando painel administrativo...</span>
        </div>
      </main>
    );
  }

  return (
    <main className="admin-dashboard-page">
      <div className="admin-dashboard-shell">
        <header className="admin-dashboard-header">
          <div>
            <span className="admin-eyebrow">Painel administrativo</span>
            <h1>ABRALIBER</h1>
            <p>Alunos, cursos, certificados e receita em uma visão operacional.</p>
          </div>
          <div className="admin-header-actions">
            <a className="admin-nav-button admin-nav-button-primary" href="#gestao-usuarios" title="Pesquisar e abrir o perfil completo de um usuário">
              <FontAwesomeIcon icon={faUsers} />
              Perfis de usuários
            </a>
            <Link className="admin-nav-button" to="/admin/percepcao-de-uso" title="Abrir jornada consolidada">
              <FontAwesomeIcon icon={faChartLine} />
              Percepção de uso
            </Link>
            <Link className="admin-nav-button" to="/admin/analytics" title="Abrir comportamento e buscas">
              <FontAwesomeIcon icon={faChartColumn} />
              Comportamento
            </Link>
            <button className="admin-icon-button" onClick={loadDashboard} type="button" title="Atualizar painel">
              <FontAwesomeIcon icon={faRotateRight} />
            </button>
          </div>
        </header>

        {error && (
          <div className="admin-alert">
            <FontAwesomeIcon icon={faTriangleExclamation} />
            <span>{error}</span>
          </div>
        )}

        <section className="admin-metrics-grid" aria-label="Indicadores principais">
          {overviewMetrics.map((item) => <MetricCard key={item.label} item={item} />)}
        </section>

        <section className="admin-overview-band">
          <div>
            <span>Inícios de curso</span>
            <strong>{number.format(dashboard?.overview?.course_starts || 0)}</strong>
          </div>
          <div>
            <span>Certificados emitidos</span>
            <strong>{number.format(dashboard?.overview?.certificates_issued || 0)}</strong>
          </div>
          <div>
            <span>Disponíveis para pagamento</span>
            <strong>{number.format(certificate.available_for_payment || 0)}</strong>
          </div>
          <div>
            <span>Tempo médio de conclusão</span>
            <strong>{number.format(coursePerformance.average_completion_days || 0)} dias</strong>
          </div>
        </section>

        <div className="admin-grid-two">
          <RankingList
            title="Cursos Mais Acessados"
            items={coursePerformance.most_accessed}
            valueKey="answers_count"
            valueLabel="interações"
          />
          <RankingList
            title="Cursos Mais Iniciados"
            items={coursePerformance.most_started}
            valueKey="starts_count"
            valueLabel="inícios"
          />
          <RankingList
            title="Cursos Mais Concluídos"
            items={coursePerformance.most_completed}
            valueKey="completions_count"
            valueLabel="conclusões"
          />
          <section className="admin-section">
            <div className="admin-section-title">
              <h2>Conclusão Por Curso</h2>
            </div>
            <div className="admin-progress-list">
              {safeList(coursePerformance.completion_by_course).slice(0, 8).map((course) => (
                <ProgressRow
                  key={course.id}
                  label={course.title}
                  value={course.completion_rate}
                  meta={`${number.format(course.completions_count)} de ${number.format(course.starts_count)} alunos`}
                />
              ))}
              {safeList(coursePerformance.completion_by_course).length === 0 && <div className="admin-empty">Sem dados para exibir.</div>}
            </div>
          </section>
        </div>

        <section className="admin-section">
          <div className="admin-section-title">
            <h2>Certificados e Receita</h2>
            <span>{number.format(certificate.payments_started || 0)} pagamentos iniciados</span>
          </div>
          <div className="admin-certificate-grid">
            <div className="admin-payment-summary">
              <div><span>Aprovados</span><strong>{number.format(certificate.payments_approved || 0)}</strong></div>
              <div><span>Recusados</span><strong>{number.format(certificate.payments_rejected || 0)}</strong></div>
              <div><span>Abandonados</span><strong>{number.format(certificate.payments_abandoned || 0)}</strong></div>
              <div><span>Ticket médio</span><strong>{currency.format(certificate.average_ticket || 0)}</strong></div>
            </div>
            <div className="admin-revenue-chart">
              {safeList(certificate.revenue_by_period).map((item) => (
                <div className="admin-revenue-bar" key={item.period}>
                  <div>
                    <span style={{ height: `${Math.max((Number(item.revenue || 0) / maxRevenue) * 100, 4)}%` }} />
                  </div>
                  <small>{item.label}</small>
                  <strong>{currency.format(item.revenue || 0)}</strong>
                </div>
              ))}
            </div>
          </div>
        </section>

        <div className="admin-grid-two">
          <RankingList
            title="Cursos Que Mais Geram Certificados"
            items={certificate.top_certificate_courses}
            valueKey="certificates_sold"
            valueLabel="vendas"
          />
          <section className="admin-section">
            <div className="admin-section-title">
              <h2>Concluíram Sem Comprar</h2>
              <span>{number.format(certificate.available_for_payment || 0)} disponível(is)</span>
            </div>
            <div className="admin-list">
              {safeList(certificate.completed_without_purchase).map((item) => (
                <div className="admin-list-row admin-list-row-stacked" key={item.id}>
                  <span className="admin-rank">{item.user?.name?.charAt(0)?.toUpperCase() || 'A'}</span>
                  <span className="admin-list-title">
                    {item.user?.name || '-'}
                    <small>{item.curso?.title || '-'} · {item.user?.email || '-'}</small>
                  </span>
                  <strong>{date(item.created_at)}</strong>
                </div>
              ))}
              {safeList(certificate.completed_without_purchase).length === 0 && <div className="admin-empty">Sem alunos pendentes.</div>}
            </div>
          </section>
        </div>

        <div className="admin-grid-two">
          <section className="admin-section">
            <div className="admin-section-title">
              <h2>Maior Abandono</h2>
            </div>
            <div className="admin-list">
              {safeList(coursePerformance.abandonment_points).map((item, index) => (
                <div className="admin-list-row" key={`${item.course_id}-${index}`}>
                  <span className="admin-rank">{index + 1}</span>
                  <span className="admin-list-title">{item.course_title}</span>
                  <strong>{number.format(item.abandoned_count || 0)} alunos</strong>
                </div>
              ))}
            </div>
          </section>
        </div>

        <section className="admin-section admin-management-section" id="gestao-usuarios">
          <div className="admin-section-title">
            <div>
              <h2>Perfis de usuários</h2>
              <p className="admin-section-description">Pesquise um aluno para abrir cadastro, cursos, certificados, currículo e diagnóstico técnico.</p>
            </div>
            <span>
              {usersLoading
                ? 'Buscando...'
                : usersMeta.total > 0
                  ? `${number.format(usersMeta.from)}–${number.format(usersMeta.to)} de ${number.format(usersMeta.total)} usuários`
                  : 'Nenhum usuário'}
            </span>
          </div>
          <div className="admin-search-box">
            <FontAwesomeIcon icon={faMagnifyingGlass} />
            <input
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setUsersPage(1);
              }}
              placeholder="Pesquisar por nome, CPF ou e-mail"
              type="search"
            />
          </div>
          <div className="admin-management-grid">
            <div className="admin-user-column">
              <div className="admin-user-list">
                {users.map((user) => (
                  <div className="admin-user-entry" key={user.id}>
                    <button
                      type="button"
                      className={`admin-user-row ${selectedUser?.id === user.id ? 'active' : ''}`}
                      onClick={() => loadUserDetail(user.id)}
                    >
                      <span>
                        <strong>{user.name}</strong>
                        <small>{user.email}</small>
                      </span>
                      <span>
                        <strong>{number.format(user.completed_courses_count || 0)}</strong>
                        <small>concluídos</small>
                      </span>
                      <span>
                        <strong>{number.format(user.paid_certificates_count || 0)}</strong>
                        <small>certificados</small>
                      </span>
                      <span>
                        <FontAwesomeIcon icon={faClock} />
                        <small>{shortDateTime(user.latest_activity_at)}</small>
                      </span>
                    </button>
                    <Link className="admin-user-direct-link" to={`/admin/usuarios/${user.id}`}>
                      Abrir perfil →
                    </Link>
                  </div>
                ))}
                {users.length === 0 && <div className="admin-empty">Nenhum usuário encontrado.</div>}
              </div>
              {usersMeta.last_page > 1 && (
                <nav className="admin-pagination" aria-label="Paginação de usuários">
                  <button
                    type="button"
                    onClick={() => setUsersPage((page) => Math.max(1, page - 1))}
                    disabled={usersLoading || usersPage <= 1}
                  >
                    ← Anterior
                  </button>
                  <span>Página {number.format(usersMeta.current_page)} de {number.format(usersMeta.last_page)}</span>
                  <button
                    type="button"
                    onClick={() => setUsersPage((page) => Math.min(usersMeta.last_page, page + 1))}
                    disabled={usersLoading || usersPage >= usersMeta.last_page}
                  >
                    Próxima →
                  </button>
                </nav>
              )}
            </div>

            <aside className="admin-user-detail">
              {selectedUser ? (
                <>
                  <div className="admin-detail-header">
                    <div>
                      <span className="admin-avatar">{selectedUser.name?.charAt(0)?.toUpperCase() || 'A'}</span>
                    </div>
                    <div>
                      <h3>{selectedUser.name}</h3>
                      <p>{selectedUser.email}</p>
                      <small>CPF {selectedUser.cpf || '-'} · cadastro {date(selectedUser.created_at)}</small>
                      <Link className="admin-profile-link" to={`/admin/usuarios/${selectedUser.id}`}>
                        Visualizar perfil completo
                      </Link>
                    </div>
                  </div>

                  <div className="admin-detail-block">
                    <h4>Cursos e progresso</h4>
                    {courseReminderMessage && <div className="admin-inline-alert">{courseReminderMessage}</div>}
                    {safeList(selectedUser.courses).map((course) => (
                      <div className="admin-detail-row" key={course.enrollment_id || course.id}>
                        <span>
                          <strong>{course.title}</strong>
                          <small>Início {date(course.started_at)} · nota {course.score ?? '-'}</small>
                        </span>
                        <div className="admin-payment-actions">
                          <StatusPill status={course.progress_status} />
                          {course.course_exam_reminder_eligible && (
                            <button
                              type="button"
                              className="admin-reminder-button"
                              onClick={() => handleCourseContinuationReminder(course)}
                              disabled={!canSendCourseReminder(course) || courseReminderSendingId === course.enrollment_id}
                              title={courseReminderBlockedLabel(course) || 'Enviar lembrete para o aluno realizar a prova'}
                            >
                              <FontAwesomeIcon icon={faEnvelope} />
                              {courseReminderSendingId === course.enrollment_id ? 'Enviando...' : 'Lembrar prova'}
                            </button>
                          )}
                          {course.course_exam_reminder_eligible && courseReminderBlockedLabel(course) && (
                            <small className="admin-reminder-note">{courseReminderBlockedLabel(course)}</small>
                          )}
                        </div>
                      </div>
                    ))}
                    {safeList(selectedUser.courses).length === 0 && <div className="admin-empty">Sem cursos.</div>}
                  </div>

                  <div className="admin-detail-block">
                    <h4>Avaliações realizadas</h4>
                    {safeList(selectedUser.assessments).map((assessment) => (
                      <div className="admin-detail-row" key={assessment.id}>
                        <span>
                          <strong>{assessment.curso?.title || '-'}</strong>
                          <small>Nota {assessment.score ?? '-'} · {date(assessment.created_at)}</small>
                        </span>
                        <StatusPill status={assessment.passed ? 'completed' : 'not_passed'} />
                      </div>
                    ))}
                    {safeList(selectedUser.assessments).length === 0 && <div className="admin-empty">Sem avaliações.</div>}
                  </div>

                  <div className="admin-detail-block">
                    <h4>Pagamentos e certificados</h4>
                    {reminderMessage && <div className="admin-inline-alert">{reminderMessage}</div>}
                    {safeList(selectedUser.payments).map((payment) => (
                      <div className="admin-detail-row" key={payment.id}>
                        <span>
                          <strong>{payment.curso?.title || '-'}</strong>
                          <small>{currency.format(payment.amount || 0)} · {date(payment.created_at)}</small>
                        </span>
                        <div className="admin-payment-actions">
                          <StatusPill status={payment.status} />
                          {payment.status === 'pending' && (
                            <button
                              type="button"
                              className="admin-reminder-button"
                              onClick={() => handleCertificateReminder(payment)}
                              disabled={!canSendCertificateReminder(payment) || reminderSendingId === payment.id}
                              title={reminderBlockedLabel(payment) || 'Enviar e-mail sugerindo o pagamento do certificado'}
                            >
                              <FontAwesomeIcon icon={faEnvelope} />
                              {reminderSendingId === payment.id ? 'Enviando...' : 'Enviar e-mail'}
                            </button>
                          )}
                          {payment.status === 'pending' && reminderBlockedLabel(payment) && (
                            <small className="admin-reminder-note">{reminderBlockedLabel(payment)}</small>
                          )}
                        </div>
                      </div>
                    ))}
                    {safeList(selectedUser.payments).length === 0 && <div className="admin-empty">Sem pagamentos.</div>}
                  </div>

                  <div className="admin-detail-block">
                    <h4>Histórico completo de eventos</h4>
                    <div className="admin-event-list">
                      {safeList(selectedUser.events).map((event) => (
                        <div className="admin-event-row" key={event.id}>
                          <span className={`admin-event-dot admin-event-dot-${event.type}`} aria-hidden="true" />
                          <span>
                            <strong>{event.title}</strong>
                            <small>{event.description || '-'} · {shortDateTime(event.created_at)}</small>
                          </span>
                        </div>
                      ))}
                      {safeList(selectedUser.events).length === 0 && (
                        <div className="admin-empty">Nenhum evento registrado.</div>
                      )}
                    </div>
                  </div>
                </>
              ) : (
                <div className="admin-user-placeholder">
                  <FontAwesomeIcon icon={faBookOpen} />
                  <span>Selecione um aluno para ver histórico, cursos, avaliações, pagamentos e certificados.</span>
                </div>
              )}
            </aside>
          </div>
        </section>
      </div>
    </main>
  );
};

export default AdminDashboard;
